import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Users, Heart, MessageSquare, BookOpen, Trophy, Plus, ThumbsUp, ChevronDown, ChevronUp, Send } from '../components/CustomIcons';

const POST_TYPES = [
  { value: 'discussion', label: 'Discussion', color: '#F97316' },
  { value: 'question', label: 'Question', color: '#3B82F6' },
  { value: 'win', label: 'Win', color: '#22C55E' },
  { value: 'resource', label: 'Resource', color: '#A855F7' },
];

const STUDY_GROUPS_MOCK = [
  { id: 'sg-1', name: 'Electrical Theory Study Group', pathway: 'Electrical Technology', members: 8, next_session: 'Tomorrow 2pm', active: true },
  { id: 'sg-2', name: 'CDL Knowledge Quiz Prep', pathway: 'CDL/Commercial Driving', members: 5, next_session: 'Friday 3pm', active: true },
  { id: 'sg-3', name: 'Cybersecurity A+ Prep', pathway: 'Cybersecurity', members: 12, next_session: 'Monday 4pm', active: true },
];

const MILESTONES_MOCK = [
  { id: 'm1', name: 'Jonathan J.', achievement: 'Completed Electrical Safety Module', detail: 'Earned OSHA 10 Certificate', created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: 'm2', name: 'David C.', achievement: 'Finished CDL Pathway', detail: '100% completion — CDL Permit Ready!', created_at: new Date(Date.now() - 86400000).toISOString() },
  { id: 'm3', name: 'Anthony B.', achievement: '73-Day Learning Streak!', detail: 'Consistency Champion Badge Earned', created_at: new Date(Date.now() - 172800000).toISOString() },
];

const MOCK_POSTS = [
  { id: 'p1', author_name: 'Jonathan J.', author_role: 'learner', content: 'Just passed my OSHA 10 quiz with 94%! The LOTO procedures really clicked for me after re-reading it twice. For anyone stuck on that section — think of it like a lock and key for your own personal safety.', post_type: 'win', pathway_tag: 'Electrical Technology', likes: 12, replies: [], created_at: new Date(Date.now() - 7200000).toISOString() },
  { id: 'p2', author_name: 'Instructor Hayes', author_role: 'instructor', content: 'Study tip for the CDL General Knowledge exam: the "Smith System" of defensive driving is guaranteed to be on the test. 5 keys: Aim High, Get the Big Picture, Keep Your Eyes Moving, Leave Yourself an Out, Make Sure They See You.', post_type: 'resource', pathway_tag: 'CDL/Commercial Driving', likes: 28, replies: [], created_at: new Date(Date.now() - 86400000).toISOString() },
  { id: 'p3', author_name: 'Carlos R.', author_role: 'learner', content: 'Anyone else find Ohm\'s Law hard to memorize? I made a trick: "Very Intelligent Robot" = V=I×R. Hope that helps someone!', post_type: 'discussion', pathway_tag: 'Electrical Technology', likes: 15, replies: [{ id: 'r1', author_name: 'Jonathan J.', content: 'That actually helped me too!', created_at: new Date(Date.now() - 3600000).toISOString() }], created_at: new Date(Date.now() - 172800000).toISOString() },
];

function timeAgo(isoStr) {
  const diff = (Date.now() - new Date(isoStr)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function PostCard({ post, onLike, onReply }) {
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [liked, setLiked] = useState(post.liked || false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);
  const type = POST_TYPES.find(t => t.value === post.post_type) || POST_TYPES[0];

  const handleLike = () => {
    setLiked(!liked);
    setLikesCount(p => liked ? p - 1 : p + 1);
    onLike(post.id);
  };

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px', marginBottom: '12px' }} data-testid={`post-${post.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #F97316, #F0943A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', color: '#111111' }}>
            {post.author_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>{post.author_name}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{timeAgo(post.created_at)}</div>
          </div>
        </div>
        <span style={{ background: `rgba(${type.color.slice(1).match(/.{2}/g).map(x => parseInt(x,16)).join(',')},0.15)`, color: type.color, fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '6px' }}>
          {type.label.toUpperCase()}
        </span>
      </div>
      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 12px' }}>{post.content}</p>
      {post.pathway_tag && (
        <span style={{ background: 'rgba(249,115,22,0.08)', color: '#F97316', fontSize: '12px', padding: '2px 10px', borderRadius: '6px', display: 'inline-block', marginBottom: '12px' }}>{post.pathway_tag}</span>
      )}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <button onClick={handleLike} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: liked ? '#F97316' : '#555555', fontSize: '13px', padding: 0 }}>
          <ThumbsUp size={14} />
          <span>{likesCount}</span>
        </button>
        <button onClick={() => setShowReplies(!showReplies)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '13px', padding: 0 }}>
          <MessageSquare size={14} />
          <span>{post.replies?.length || 0} {post.replies?.length === 1 ? 'reply' : 'replies'}</span>
        </button>
      </div>

      {showReplies && (
        <div style={{ marginTop: '16px', borderTop: '1px solid #1A1A1A', paddingTop: '16px' }}>
          {(post.replies || []).map(r => (
            <div key={r.id} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#777777', flexShrink: 0 }}>
                {r.author_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#F97316', fontWeight: 600, marginBottom: '2px' }}>{r.author_name}</div>
                <p style={{ fontSize: '13px', color: '#AAAAAA', margin: 0 }}>{r.content}</p>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <input
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Write a reply..."
              style={{ flex: 1, background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '8px 12px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }}
              onKeyDown={e => { if (e.key === 'Enter' && replyText.trim()) { onReply(post.id, replyText); setReplyText(''); } }}
            />
            <button onClick={() => { if (replyText.trim()) { onReply(post.id, replyText); setReplyText(''); } }}
              style={{ background: '#F97316', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', color: '#111111', display: 'flex', alignItems: 'center' }}>
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Community() {
  const { authFetch, token } = useAuth();
  const [posts, setPosts] = useState(MOCK_POSTS);
  const [showCompose, setShowCompose] = useState(false);
  const [newPost, setNewPost] = useState({ content: '', post_type: 'discussion', pathway_tag: '' });
  const [tab, setTab] = useState('feed');
  const [liveConnected, setLiveConnected] = useState(false);

  useEffect(() => {
    // Initial load
    authFetch('/community/posts')
      .then(r => { if (r.data?.length > 0) setPosts(r.data); })
      .catch(() => {});

    // WebSocket for real-time updates
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/api/community/ws';
    let ws;
    let pingInterval;
    let fallbackInterval;
    let wsReady = false;

    const connectWs = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          setLiveConnected(true);
          wsReady = true;
          // Send keep-alive ping every 25s
          pingInterval = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000);
        };
        ws.onmessage = (e) => {
          if (e.data === 'pong') return;
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'new_post' && msg.post) {
              setPosts(prev => {
                if (prev.some(p => p.id === msg.post.id)) return prev;
                return [msg.post, ...prev];
              });
            }
          } catch (e) { console.error('WS message parse error:', e); }
        };
        ws.onerror = () => { wsReady = false; };
        ws.onclose = () => {
          setLiveConnected(false);
          wsReady = false;
          clearInterval(pingInterval);
          // Fall back to polling on close
          if (!fallbackInterval) {
            fallbackInterval = setInterval(() => {
              authFetch('/community/posts')
                .then(r => { if (r.data?.length > 0) setPosts(r.data); })
                .catch(() => {});
            }, 15000);
          }
        };
      } catch (_) {
        // WebSocket not supported, use polling fallback
        fallbackInterval = setInterval(() => {
          authFetch('/community/posts')
            .then(r => { if (r.data?.length > 0) setPosts(r.data); })
            .catch(() => {});
        }, 15000);
      }
    };

    connectWs();

    return () => {
      clearInterval(pingInterval);
      clearInterval(fallbackInterval);
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePost = async () => {
    if (!newPost.content.trim()) return;
    try {
      const r = await authFetch('/community/posts', { method: 'POST', data: newPost });
      setPosts(prev => [r.data, ...prev]);
      setNewPost({ content: '', post_type: 'discussion', pathway_tag: '' });
      setShowCompose(false);
    } catch (e) { console.error('post-create error:', e); }
  };

  const handleLike = (postId) => {
    authFetch(`/community/posts/${postId}/like`, { method: 'POST' }).catch(() => {});
  };

  const handleReply = async (postId, content) => {
    try {
      const r = await authFetch(`/community/posts/${postId}/reply`, { method: 'POST', data: { content } });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, replies: [...(p.replies || []), r.data] } : p));
    } catch (e) { console.error('reply error:', e); }
  };

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="community-page">
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Community</h1>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif', background: liveConnected ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)', color: liveConnected ? '#22C55E' : '#555', border: `1px solid ${liveConnected ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)'}` }} data-testid="live-indicator">
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: liveConnected ? '#22C55E' : '#555', display: 'inline-block', animation: liveConnected ? 'pulse 2s infinite' : 'none' }} />
                {liveConnected ? 'LIVE' : 'OFFLINE'}
              </span>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>Support, share, and grow together.</p>
          </div>
          <button onClick={() => setShowCompose(!showCompose)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F97316', border: 'none', borderRadius: '10px', padding: '10px 18px', cursor: 'pointer', color: '#111111', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px' }} data-testid="compose-post-btn">
            <Plus size={16} /> Post
          </button>
        </div>

        {/* Compose */}
        {showCompose && (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '20px', marginBottom: '20px' }} data-testid="compose-post-form">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {POST_TYPES.map(t => (
                <button key={t.value} onClick={() => setNewPost(p => ({ ...p, post_type: t.value }))}
                  style={{ padding: '5px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: `1px solid ${newPost.post_type === t.value ? t.color : '#333333'}`, background: newPost.post_type === t.value ? `rgba(${t.color.slice(1).match(/.{2}/g).map(x => parseInt(x,16)).join(',')},0.15)` : 'transparent', color: newPost.post_type === t.value ? t.color : '#555555' }}>
                  {t.label}
                </button>
              ))}
            </div>
            <label htmlFor="post-content" className="sr-only">Share your message with the community</label>
            <textarea
              id="post-content"
              value={newPost.content}
              onChange={e => setNewPost(p => ({ ...p, content: e.target.value }))}
              placeholder="Share a win, ask a question, or share a resource..."
              rows={4}
              aria-label="Community post content"
              style={{ width: '100%', background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '12px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', fontFamily: 'Inter, sans-serif', resize: 'vertical', boxSizing: 'border-box' }}
              data-testid="post-content-input"
            />
            <label htmlFor="pathway-tag-input" className="sr-only">Pathway tag (optional)</label>
            <input
              id="pathway-tag-input"
              value={newPost.pathway_tag}
              onChange={e => setNewPost(p => ({ ...p, pathway_tag: e.target.value }))}
              placeholder="Pathway tag (optional, e.g. Electrical Technology)"
              aria-label="Pathway tag (optional)"
              style={{ width: '100%', background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '10px 12px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', marginTop: '10px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
              <button onClick={() => setShowCompose(false)} style={{ background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px' }} aria-label="Cancel post">Cancel</button>
              <button onClick={handlePost} style={{ background: '#F97316', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', color: '#111111', fontWeight: 600, fontSize: '13px', fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="submit-post-btn" aria-label="Publish post">Post</button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-surface)', borderRadius: '10px', padding: '4px', marginBottom: '20px', width: 'fit-content' }}>
          {['feed', 'milestones', 'groups'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              aria-pressed={tab === t}
              style={{ padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none', background: tab === t ? '#F97316' : 'transparent', color: tab === t ? '#111111' : '#666666', fontFamily: 'Plus Jakarta Sans, sans-serif', textTransform: 'capitalize' }}>
              {t}
            </button>
          ))}
        </div>

        {/* Feed */}
        {tab === 'feed' && (
          <div aria-live="polite" aria-label="Community posts feed">
            {posts.map(post => (
              <PostCard key={post.id} post={post} onLike={handleLike} onReply={handleReply} />
            ))}
          </div>
        )}

        {/* Milestones */}
        {tab === 'milestones' && (
          <div data-testid="milestones-tab">
            {MILESTONES_MOCK.map(m => (
              <div key={m.id} style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px', marginBottom: '12px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Trophy size={20} color="#F97316" />
                </div>
                <div>
                  <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '4px' }}>{m.name}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{m.achievement}</div>
                  <div style={{ fontSize: '13px', color: '#F97316' }}>{m.detail}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '6px' }}>{timeAgo(m.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Study Groups */}
        {tab === 'groups' && (
          <div data-testid="study-groups-tab">
            {STUDY_GROUPS_MOCK.map(g => (
              <div key={g.id} style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Users size={18} color="#F97316" />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '4px' }}>{g.name}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{g.pathway}</div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      <span><Users size={11} style={{ display: 'inline', marginRight: '4px' }} />{g.members} members</span>
                      <span>Next: {g.next_session}</span>
                    </div>
                  </div>
                </div>
                <button style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', color: '#F97316', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Join
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
